import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { MessageSquare, Search, ArrowUpRight, ArrowDownLeft, Loader2, Check, Reply, X, Send, BarChart2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TemplateChips } from '../components/intelligence/TemplateChips';
import { SmsThreadDialog } from '../components/custom/SmsThreadDialog';
import { SmsAlertSettings } from '../components/custom/SmsAlertSettings';
import { useSmsInboundSocket } from '../hooks/useSmsInboundSocket';
import { playReplyChime } from '../lib/replyChime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ago = (iso) => {
  if (!iso) return '';
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 60) return `${m}m ago`;
  if (m < 1440) return `${Math.round(m / 60)}h ago`;
  return `${Math.round(m / 1440)}d ago`;
};

const OUTCOME_LABELS = { won: 'Won', follow_up_booked: 'Call booked', no_response: 'No response', not_interested: 'Not interested' };

const TeamActivityCard = () => {
  const [report, setReport] = useState(null);
  const [reportDays, setReportDays] = useState(7);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/sms/activity-report`, { params: { days: reportDays } })
      .then((r) => setReport(r.data.rows || []))
      .catch(() => setReport([]));
  }, [open, reportDays]);

  return (
    <div className="bg-white border border-slate-200 rounded-xl mb-4" data-testid="sms-activity-card">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        data-testid="sms-activity-toggle">
        <span className="text-sm font-semibold text-slate-800 flex items-center gap-2">
          <BarChart2 className="w-4 h-4 text-teal-600" /> Team SMS Activity
        </span>
        <span className="text-xs text-slate-400">{open ? 'Hide' : 'Show'}</span>
      </button>
      {open && (
        <div className="px-4 pb-4">
          <div className="flex items-center gap-1.5 mb-3">
            {[[7, 'Last 7 days'], [30, 'Last 30 days']].map(([v, label]) => (
              <button key={v} onClick={() => setReportDays(v)}
                className={`text-xs font-semibold px-3 py-1 rounded-full ${reportDays === v ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                data-testid={`sms-activity-days-${v}`}>{label}</button>
            ))}
          </div>
          {report === null ? (
            <Loader2 className="w-5 h-5 animate-spin text-teal-500 mx-auto my-4" />
          ) : report.length === 0 ? (
            <p className="text-sm text-slate-400 py-2" data-testid="sms-activity-empty">No SMS sent by your team in this window.</p>
          ) : (
            <table className="w-full text-sm" data-testid="sms-activity-table">
              <thead>
                <tr className="text-[11px] uppercase text-slate-400 text-left">
                  <th className="pb-1.5 font-semibold">Sender</th>
                  <th className="pb-1.5 font-semibold text-right">Sent</th>
                  <th className="pb-1.5 font-semibold text-right">Replies</th>
                  <th className="pb-1.5 font-semibold text-right">Reply rate</th>
                  <th className="pb-1.5 font-semibold text-right">Last sent</th>
                </tr>
              </thead>
              <tbody>
                {report.map((r) => (
                  <tr key={r.sender} className="border-t border-slate-100" data-testid={`sms-activity-row-${r.sender}`}>
                    <td className="py-1.5 text-slate-800 font-medium">{r.sender}</td>
                    <td className="py-1.5 text-right text-slate-700">{r.sent}</td>
                    <td className="py-1.5 text-right text-slate-700">{r.replies}</td>
                    <td className="py-1.5 text-right">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${r.reply_rate >= 30 ? 'bg-emerald-100 text-emerald-700' : r.reply_rate > 0 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
                        {r.reply_rate}%
                      </span>
                    </td>
                    <td className="py-1.5 text-right text-xs text-slate-400">{ago(r.last_sent)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default function SmsInboxPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [counts, setCounts] = useState({ sent: 0, received: 0 });
  const [direction, setDirection] = useState('all');
  const [days, setDays] = useState(90);
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [draft, setDraft] = useState('');
  const [picked, setPicked] = useState(null);
  const [sending, setSending] = useState(false);
  const [threadPhone, setThreadPhone] = useState(null);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const t = searchParams.get('thread');
    if (t) setThreadPhone(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/sms/inbox`, { params: { days, direction, q: search } })
      .then((r) => {
        setRows(r.data.rows || []);
        setCounts({ sent: r.data.sent_count || 0, received: r.data.received_count || 0 });
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [days, direction, search]);
  useEffect(() => { load(); }, [load]);

  useSmsInboundSocket(useCallback((inb) => {
    playReplyChime();
    toast.info(`New SMS from ${inb.from_phone}: “${(inb.content || '').slice(0, 60)}”`);
    load();
  }, [load]));

  const isAgent = !['master_admin', 'domain_admin', 'manager', 'admin', 'domain_supervisor', 'supervisor', 'office_manager'].includes(user?.role);
  const isManager = !isAgent;

  const sendReply = async (row) => {
    if (!draft.trim()) return;
    setSending(true);
    try {
      const templateId = picked && draft.trim() === picked.content ? picked.id : null;
      await axios.post(`${API}/sms/replies/${row.id}/quick-reply`, { content: draft.trim(), template_id: templateId });
      toast.success('Reply sent');
      setReplyingTo(null); setDraft(''); setPicked(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    setSending(false);
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="sms-inbox-page">
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
          <MessageSquare className="w-6 h-6 text-teal-600" /> SMS Inbox
        </h1>
        <p className="text-sm text-slate-500 mt-0.5" data-testid="sms-inbox-scope-note">
          {isAgent
            ? 'Your own SMS conversations — messages you sent and the replies to them.'
            : user?.role === 'domain_supervisor'
              ? 'SMS conversations for the agents you monitor.'
              : 'Every SMS sent and received across your tenant.'}
        </p>
      </div>

      {isManager && <TeamActivityCard />}

      <div className="flex items-center gap-3 flex-wrap mb-4 bg-white border border-slate-200 rounded-xl px-4 py-3">
        <div className="flex items-center gap-1.5" data-testid="sms-inbox-direction-pills">
          {[['all', 'All'], ['received', 'Received'], ['sent', 'Sent']].map(([v, label]) => (
            <button key={v} onClick={() => setDirection(v)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full ${direction === v ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              data-testid={`sms-inbox-pill-${v}`}>
              {label}
            </button>
          ))}
        </div>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))}
          className="text-xs font-semibold border border-slate-200 rounded-md px-2 py-1.5 text-slate-700 bg-white"
          data-testid="sms-inbox-days-select">
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={180}>Last 180 days</option>
        </select>
        <div className="flex items-center gap-1.5 flex-1 min-w-[220px]">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') setSearch(q.trim()); }}
              placeholder="Search phone number or message text…"
              className="w-full text-sm text-slate-900 bg-white border border-slate-200 rounded-md pl-8 pr-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-teal-400"
              data-testid="sms-inbox-search-input" />
          </div>
          <button onClick={() => setSearch(q.trim())}
            className="text-xs font-semibold px-3 py-1.5 rounded-md bg-slate-800 hover:bg-slate-700 text-white"
            data-testid="sms-inbox-search-btn">Search</button>
          {search && (
            <button onClick={() => { setQ(''); setSearch(''); }}
              className="text-xs text-slate-400 hover:text-slate-600" data-testid="sms-inbox-search-clear">Clear</button>
          )}
        </div>
        <span className="text-[11px] text-slate-400" data-testid="sms-inbox-counts">
          {counts.sent} sent · {counts.received} received
        </span>
        <SmsAlertSettings />
      </div>

      {loading || rows === null ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-teal-500" /></div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl px-6 py-10 text-center" data-testid="sms-inbox-empty">
          <MessageSquare className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">No SMS {search ? 'matching your search' : 'in this window'}.</p>
        </div>
      ) : (
        <div className="space-y-1.5" data-testid="sms-inbox-list">
          {rows.map((r) => (
            <div key={`${r.kind}-${r.id}`}
              className={`bg-white border rounded-lg px-4 py-2.5 flex items-start gap-3 flex-wrap ${r.kind === 'received' && !r.handled ? 'border-amber-300' : 'border-slate-200'}`}
              data-testid={`sms-inbox-row-${r.id}`}>
              {r.kind === 'sent'
                ? <ArrowUpRight className="w-4 h-4 text-teal-500 mt-1 shrink-0" title="Sent" />
                : <ArrowDownLeft className="w-4 h-4 text-amber-500 mt-1 shrink-0" title="Received" />}
              <div className="flex-1 min-w-[240px]">
                <p className="text-sm text-slate-900 flex items-center gap-2 flex-wrap">
                  <button onClick={() => setThreadPhone(r.phone)}
                    className="font-semibold hover:text-teal-600 hover:underline underline-offset-2"
                    title="View full conversation"
                    data-testid={`sms-inbox-thread-${r.id}`}>{r.phone}</button>
                  <span className="text-xs text-slate-400">· {ago(r.at)}</span>
                  {r.kind === 'sent' && r.agent && <span className="text-[11px] text-slate-400">by {r.agent}</span>}
                </p>
                <p className="text-sm text-slate-700 mt-0.5">“{r.content}”</p>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {r.kind === 'sent' && r.delivery_status && (
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">{r.delivery_status}</span>
                )}
                {r.kind === 'sent' && r.outcome && (
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">{OUTCOME_LABELS[r.outcome] || r.outcome}</span>
                )}
                {r.kind === 'received' && (
                  r.handled
                    ? <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 inline-flex items-center gap-0.5"><Check className="w-3 h-3" /> handled{r.handled_by ? ` · ${r.handled_by.split('@')[0]}` : ''}</span>
                    : <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">needs reply</span>
                )}
                {r.kind === 'received' && !r.handled && user?.sms_access === true && (
                  <button onClick={() => { setReplyingTo(replyingTo === r.id ? null : r.id); setDraft(''); setPicked(null); }}
                    className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-500 text-white hover:bg-amber-400 inline-flex items-center gap-0.5"
                    data-testid={`sms-inbox-reply-${r.id}`}>
                    {replyingTo === r.id ? <X className="w-3 h-3" /> : <Reply className="w-3 h-3" />} {replyingTo === r.id ? 'Cancel' : 'Reply'}
                  </button>
                )}
                {r.call_id && (
                  <button onClick={() => navigate(`/calls/${r.call_id}`)}
                    className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-800 text-white hover:bg-slate-700"
                    data-testid={`sms-inbox-open-call-${r.id}`}>Open call</button>
                )}
              </div>
              {r.kind === 'received' && replyingTo === r.id && (
                <div className="w-full mt-2" data-testid={`sms-inbox-composer-${r.id}`}>
                  <TemplateChips domainId={r.domain_id} onPick={(t) => { setDraft(t.content); setPicked(t); }} isManager={isManager} />
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <textarea value={draft} onChange={(e) => setDraft(e.target.value.slice(0, 459))} rows={2} autoFocus
                        placeholder={`Text ${r.phone} back…`}
                        className="w-full text-sm text-slate-900 bg-white placeholder:text-slate-400 border border-amber-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
                        data-testid={`sms-inbox-composer-input-${r.id}`} />
                      <p className="text-[10px] text-slate-400 text-right">{draft.length}/459</p>
                    </div>
                    <button onClick={() => sendReply(r)} disabled={sending || !draft.trim()}
                      className="text-xs font-semibold px-3 py-2 mb-4 rounded-md bg-teal-600 hover:bg-teal-500 disabled:opacity-50 text-white inline-flex items-center gap-1"
                      data-testid={`sms-inbox-send-${r.id}`}>
                      <Send className="w-3.5 h-3.5" /> {sending ? 'Sending…' : 'Send SMS'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <SmsThreadDialog phone={threadPhone} onClose={() => setThreadPhone(null)} />
    </div>
  );
}
